"use client";
import React, { useState, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import { fetchWrapper } from "@/helpers";
import Quantity from "./Quantity";
import SliderWithGalleryPopup from "./sliders/SliderWithGalleryPopup";
import { useContextElement } from "@/context/Context";

import { getProductCoin, getProductLanguage, getProductPrice, useT } from "@/context/I18nContext";
import { usePWA } from "@/utlis/usePWA";
import { useBottomBarVisible } from "@/utlis/useBottomBarVisible";
import { Heart, ShoppingBag, X } from "lucide-react";

export default function DetailsPopup({ product }) {
	const isPWA = usePWA();
	const bottomBarVisible = useBottomBarVisible(true);
	const [selectedVariant, setSelectedVariant] = useState(product);
	const [quantity, setQuantity] = useState(1);
	const [liveViewers, setLiveViewers] = useState(null);

	useEffect(() => {
		setLiveViewers(Math.floor(Math.random() * 20) + 1);
	}, [product?.id]);

	useEffect(() => {
		setSelectedVariant(product);
		window.scrollTo(0, 0);
	}, [product?.id]);

	useEffect(() => {
		document.body.classList.add("tf-product-page");
		return () => document.body.classList.remove("tf-product-page");
	}, []);

	const variantOptions = useMemo(() => {
		if (!product?.variation_group) return [];
		const self = {
			id: product.id,
			sku: product.sku,
			name: product.name,
			variation_value: product.variation_value,
			price: product.price,
			priceReduced: product.priceReduced,
			stock: product.stock,
			images: product.images,
		};
		const siblings = Array.isArray(product.variationSiblings) ? product.variationSiblings : [];
		const merged = [self, ...siblings.filter((s) => s.id !== product.id)];
		return merged.sort((a, b) => (a.variation_value || "").localeCompare(b.variation_value || ""));
	}, [product]);

	const {
		addProductToCart,
		isAddedToCartProducts,
		addToWishlist,
		isAddedtoWishlist,
	} = useContextElement();

     const { t } = useT(); 

	const showSellerInfo = async () => {
		const EMAGYL_INFO = {
			companyName: "EMAGYL S.R.L.",
			cui: "54350609",
			regCom: "J2026020240002",
			address: "Ilfov, Oraș Bragadiru, Str. Verii, Nr. 38A, Cod 077025",
			email: "contact@emagyl.ro",
			phone: "021 555 1309",
			avatar: "/images/logo/emagyl.png",
		};

		let info = EMAGYL_INFO;
		let memberSince = null;

		// 1. Vânzător marketplace real (cont propriu, verificat)
		if (product.listedBy && Number(product.listedBy) > 0) {
			try {
				const data = await fetchWrapper.get(`/api/sellers/info?userID=${product.listedBy}`);
				if (data.success && data.seller) {
					info = {
						companyName: data.seller.nume_firma || `${data.seller.firstName} ${data.seller.lastName}`,
						cui: data.seller.cui_firma || "—",
						regCom: data.seller.nr_inregistrare_firma || "—",
						address: data.seller.adresa_firma || "—",
						email: data.seller.email || "—",
						phone: data.seller.phone || "—",
						avatar: data.seller.marketplace_avatar || "/images/logo/emagyl.png",
					};
					memberSince = data.seller.createdAt;
				}
			} catch (error) {
				console.error("Eroare la obținerea datelor vânzătorului:", error);
			}
		}

		// 2. Furnizor din spate (AVEX/DEPOX/BIGBUY etc) — dacă nu e vânzător marketplace real
		if (info === EMAGYL_INFO && product.back_supplier) {
			try {
				const data = await fetchWrapper.get(`/api/suppliers/info?code=${product.back_supplier}`);
				if (data.success && data.supplier) {
					info = {
						companyName: data.supplier.companyName || "—",
						cui: data.supplier.cui || "—",
						regCom: data.supplier.regCom || "—",
						address: data.supplier.address || "—",
						addressDepozit: data.supplier.addressDepozit,
						banca: data.supplier.banca,
						contRon: data.supplier.contRon,
						contEur: data.supplier.contEur,
						email: data.supplier.email || "—",
						phone: data.supplier.phone,
						note: data.supplier.note,
						avatar: "/images/logo/emagyl.png",
					};
				}
			} catch (error) {
				console.error("Eroare la obținerea datelor furnizorului:", error);
			}
		}

		Swal.fire({
			title: info.companyName,
			html: `
				<div style="text-align:left; font-size:14px; line-height:1.8;">
					<p><strong>Denumire firmă:</strong> ${info.companyName}</p>
					<p><strong>CUI:</strong> ${info.cui}</p>
					<p><strong>Nr. Reg. Com.:</strong> ${info.regCom}</p>
					<p><strong>Adresă:</strong> ${info.address}</p>
					${info.addressDepozit ? `<p><strong>Depozit:</strong> ${info.addressDepozit}</p>` : ""}
					${info.phone ? `<p><strong>Telefon:</strong> ${info.phone}</p>` : ""}
					<p><strong>Email:</strong> ${info.email}</p>
					${info.banca ? `<p><strong>Bancă:</strong> ${info.banca}</p>` : ""}
					${info.contRon ? `<p><strong>Cont RON:</strong> ${info.contRon}</p>` : ""}
					${info.contEur ? `<p><strong>Cont EUR:</strong> ${info.contEur}</p>` : ""}
					${info.note ? `<p style="font-size:12px; color:#666;">${info.note}</p>` : ""}
					${memberSince ? `<p><strong>Partener din:</strong> ${new Date(memberSince).toLocaleDateString("ro-RO")}</p>` : ""}
				</div>
			`,
			confirmButtonText: "Închide",
			confirmButtonColor: "#000",
			width: 500,
		});
	};

	const copyURL = () => {
		if (navigator.clipboard) {
			navigator.clipboard
				.writeText(window.location.href)
				.then(() => alert("Link copiat!"))
				.catch((err) =>
					console.error("Eroare la copierea linkului:", err)
				);
		} else {
			console.error("Clipboard API nu este suportat.");
		}
	};

	const showDeliveryInfo = () => {
		Swal.fire({
			title: t("produs_livrare"),
			html: `
				<div style="text-align:left; font-size:14px; line-height:1.8;">
					<p>${t("produs_timp_livrare")}</p>
					<p>${t("produs_retur_acceptat")}</p>
				</div>
			`,
			confirmButtonText: "Închide",
			confirmButtonColor: "#000",
			width: 500,
		});
	};

	return (
		<section
			className='flat-spacing-4 pt_0'
			style={{ maxWidth: "100vw", overflow: "clip" }}
		>
			<div className='tf-main-product section-image-zoom'>
				<div className={isPWA ? "container-fluid px-0" : "container-full px_15 lg-px_40 tf-product-page-container"}>
					<div className={isPWA ? "row g-0" : "row"}>
						<div className={isPWA ? "col-12 px-0" : "col-md-6"}>
							<div className='tf-product-media-wrap sticky-top'>
								<div className='thumbs-slider' style={isPWA ? { display: "block" } : undefined}>
									<SliderWithGalleryPopup
										key={selectedVariant.id}
										images={selectedVariant.images?.split('|').filter(Boolean)}
										isPWA={isPWA}
									/>
								</div>
							</div>
						</div>
						<div className={isPWA ? "col-12" : "col-md-6"} style={isPWA ? { padding: "16px 28px 0" } : undefined}>
							<div className='tf-product-info-wrap position-relative'>
								<div className='tf-zoom-main' />
								<div className='tf-product-info-list other-image-zoom' style={isPWA ? { paddingLeft: 0 } : undefined}>
									<div className='tf-product-info-title'>
										<h1>{getProductLanguage(product)}</h1>
									</div>

									<div className='tf-product-buybox' id='product-buy-box'>

									<div className='tf-product-info-price'>
										<div className='price-on-sale' style={{ color: '#2b9d31ff' }}>
											{selectedVariant.priceReduced ? getProductPrice(selectedVariant.priceReduced) : getProductPrice(selectedVariant.price) } {getProductCoin()}
										</div>
										<div className='compare-at-price'>
											{selectedVariant.priceReduced ? getProductPrice(selectedVariant.price) : ""}
										</div>
										<div className='badges-on-sale'>
											{selectedVariant.price &&
											selectedVariant.priceReduced ? (
												<span>
													{(
														((selectedVariant.price -
															selectedVariant.priceReduced) /
															selectedVariant.price) *
														100
													).toFixed(0)}
													% REDUCERE
												</span>
											) : (
												<></>
											)}
										</div>
									</div>

									{liveViewers !== null && (
										<div className='tf-product-info-liveview'>
											<div className='liveview-count'>{liveViewers}</div>
											<p className='fw-6 mb-0'>{t("produs_vizualizeaza_acum")}</p>
										</div>
									)}

                                             {selectedVariant.stock >= 1 ? (
                                                  <>
                                                       {variantOptions.length > 1 && (
                                                       <div className='tf-product-info-variant-picker'>
                                                            <div className='variant-picker-item'>
                                                                 <div className='d-flex justify-content-between align-items-center'>
                                                                      <div className='variant-picker-label'>
                                                                           Alege{" "}
                                                                           {product.variation_attribute || "varianta"}
                                                                      </div>
                                                                 </div>
                                                                 <form className='variant-picker-values'>
                                                                      {variantOptions.map((option) => (
                                                                           <React.Fragment key={option.id}>
                                                                                <input
                                                                                     type='radio'
                                                                                     name='variation'
                                                                                     id={`variant-${option.id}`}
                                                                                     readOnly
                                                                                     disabled={option.stock < 1}
                                                                                     checked={selectedVariant.id === option.id}
                                                                                />
                                                                                <label
                                                                                     onClick={() =>
                                                                                          option.stock >= 1 &&
                                                                                          setSelectedVariant(option)
                                                                                     }
                                                                                     className='style-text'
                                                                                     htmlFor={`variant-${option.id}`}
                                                                                     data-value={option.variation_value}
                                                                                     style={option.stock < 1 ? { opacity: 0.4, cursor: "not-allowed" } : {}}
                                                                                >
                                                                                     <p>{option.variation_value}</p>
                                                                                </label>
                                                                           </React.Fragment>
                                                                      ))}
                                                                 </form>
                                                            </div>
                                                       </div>
                                                       )}
                                                       <div className='tf-product-info-quantity'>
                                                            <div className='quantity-title fw-6'>
                                                                 {t("produs_cantitate")}
                                                            </div>
                                                            <Quantity
                                                                 key={selectedVariant.id}
                                                                 setQuantity={setQuantity}
                                                                 maxQuantity={selectedVariant.stock}
                                                            />
                                                       </div>
                                                       <div className='tf-product-inline-actionbar hide-on-mobile'>
                                                            <button
                                                                 type='button'
                                                                 className='tf-inline-actionbar-wishlist'
                                                                 onClick={() =>
                                                                      addProductToCart(
                                                                           selectedVariant.id,
                                                                           quantity ? quantity : 1
                                                                      )
                                                                 }
                                                                 style={{
                                                                      appearance: "none",
                                                                      WebkitAppearance: "none",
                                                                      boxShadow: "none",
                                                                      backgroundImage: "none",
                                                                      borderRadius: 10,
                                                                      border: 0,
                                                                      background: isAddedToCartProducts(selectedVariant.id) ? "#db1215" : "#3b82f6",
                                                                      color: "#fff",
                                                                 }}>
                                                                 {isAddedToCartProducts(selectedVariant.id) ? (
                                                                      <X size={20} />
                                                                 ) : (
                                                                      <ShoppingBag size={20} />
                                                                 )}
                                                                 {isAddedToCartProducts(selectedVariant.id)
                                                                      ? t("produs_scoate_din_cos")
                                                                      : t("produs_adauga_in_cos")}
                                                            </button>

                                                            <button
                                                                 type='button'
                                                                 className='tf-inline-actionbar-wishlist'
                                                                 onClick={() => addToWishlist(selectedVariant.id)}
                                                                 aria-label='Adaugă la favorite'
                                                                 style={{
                                                                      appearance: "none",
                                                                      WebkitAppearance: "none",
                                                                      boxShadow: "none",
                                                                      backgroundImage: "none",
                                                                      borderRadius: 10,
                                                                      border: "1px solid #e5e5e5",
                                                                      background: "#fff",
                                                                 }}>
                                                                 <Heart
                                                                      size={20}
                                                                      fill={isAddedtoWishlist(selectedVariant.id) ? "#e53e3e" : "none"}
                                                                      color={isAddedtoWishlist(selectedVariant.id) ? "#e53e3e" : "#6b7280"}
                                                                 />
                                                                 <span className='tf-inline-actionbar-wishlist-text'>
                                                                      {isAddedtoWishlist(selectedVariant.id)
                                                                           ? t("produs_adaugat_favorite")
                                                                           : t("produs_adauga_favorite")}
                                                                 </span>
                                                            </button>
                                                       </div>
                                                  </>
                                             ):(
                                                  <div> 
                                                       <b style={{ color: 'red' }}>{t("produs_out_of_stock")}</b>
                                                  </div> 
                                             )} 

									<div className='tf-product-info-extra-link'>
										<a
											href='#'
											className='tf-product-extra-icon'
											onClick={(e) => {
												e.preventDefault();
												showDeliveryInfo();
											}}
										>
											<div className='icon'>
												<svg
													className='d-inline-block'
													xmlns='http://www.w3.org/2000/svg'
													width={22}
													height={18}
													viewBox='0 0 22 18'
													fill='currentColor'
												>
													<path d='M21.7872 10.4724C21.7872 9.73685 21.5432 9.00864 21.1002 8.4217L18.7221 5.27043C18.2421 4.63481 17.4804 4.25532 16.684 4.25532H14.9787V2.54885C14.9787 1.14111 13.8334 0 12.4255 0H9.95745V1.69779H12.4255C12.8948 1.69779 13.2766 2.07962 13.2766 2.54885V14.5957H8.15145C7.80021 13.6052 6.85421 12.8936 5.74468 12.8936C4.63515 12.8936 3.68915 13.6052 3.33792 14.5957H2.55319C2.08396 14.5957 1.70213 14.2139 1.70213 13.7447V2.54885C1.70213 2.07962 2.08396 1.69779 2.55319 1.69779H9.95745V0H2.55319C1.14528 0 0 1.14111 0 2.54885V13.7447C0 15.1526 1.14528 16.2979 2.55319 16.2979H3.33792C3.68915 17.2884 4.63515 18 5.74468 18C6.85421 18 7.80021 17.2884 8.15145 16.2979H13.423C13.7742 17.2884 14.7202 18 15.8297 18C16.9393 18 17.8853 17.2884 18.2365 16.2979H21.7872V10.4724ZM16.684 5.95745C16.9494 5.95745 17.2034 6.08396 17.3634 6.29574L19.5166 9.14894H14.9787V5.95745H16.684ZM5.74468 16.2979C5.27545 16.2979 4.89362 15.916 4.89362 15.4468C4.89362 14.9776 5.27545 14.5957 5.74468 14.5957C6.21392 14.5957 6.59575 14.9776 6.59575 15.4468C6.59575 15.916 6.21392 16.2979 5.74468 16.2979ZM15.8298 16.2979C15.3606 16.2979 14.9787 15.916 14.9787 15.4468C14.9787 14.9776 15.3606 14.5957 15.8298 14.5957C16.299 14.5957 16.6809 14.9776 16.6809 15.4468C16.6809 15.916 16.299 16.2979 15.8298 16.2979ZM18.2366 14.5957C17.8853 13.6052 16.9393 12.8936 15.8298 12.8936C15.5398 12.8935 15.252 12.943 14.9787 13.04V10.8511H20.0851V14.5957H18.2366Z' />
												</svg>
											</div>
											<div className='text fw-6'>
												{t("produs_livrare")}
											</div>
										</a>
										<a
											href=''
											className='tf-product-extra-icon tf-share-btn'
											onClick={(e) => {
												e.preventDefault();
												copyURL();
											}}
										>
											<div className='icon'>
												<i className='icon-share' />
											</div>
											<div className='text fw-6'>
												{t("produs_distribuie")}
											</div>
										</a>
					
									</div> 

                                             {product.supplier === product.back_supplier && (
                                                  <>
                                                       <div className='tf-product-info-supplier'>{t("produs_vandut_de")} <strong onClick={showSellerInfo} style={{ cursor: "pointer", textDecoration: "underline" }}>{product.back_supplier}</strong> {t("produs_vat")}</div>
                                                  </>
                                             )}

                                             {product.supplier != product.back_supplier && (
                                                  <>
                                                       <div className='tf-product-info-supplier'>{t("produs_vandut_de")} <strong onClick={showSellerInfo} style={{ cursor: "pointer", textDecoration: "underline" }}>{product.supplier}</strong> {t("produs_prin")} {product.back_supplier} {t("produs_vat")}</div>
                                                  </>
                                             )}

									<div className='tf-product-info-trust-seal'></div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>{" "}

				{selectedVariant.stock >= 1 && isPWA && (
					<div
						className='tf-product-mobile-actionbar'
						style={{
							position: "fixed",
							left: 16,
							right: 16,
							bottom: "calc(env(safe-area-inset-bottom) + 92px)",
							transform: bottomBarVisible ? "translateY(0)" : "translateY(72px)",
							transition: "transform 0.28s ease",
							display: "flex",
							gap: 10,
							zIndex: 9000,
						}}>
						<button
							type='button'
							onClick={() => addToWishlist(selectedVariant.id)}
							aria-label='Adaugă la favorite'
							style={{
								flexShrink: 0,
								width: 54,
								height: 54,
								borderRadius: "50%",
								border: 0,
								background: "#fff",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								boxShadow: "0 10px 30px rgba(15, 23, 42, 0.18), 0 2px 8px rgba(15, 23, 42, 0.1)",
							}}>
							<Heart
								size={22}
								fill={isAddedtoWishlist(selectedVariant.id) ? "#e53e3e" : "none"}
								color={isAddedtoWishlist(selectedVariant.id) ? "#e53e3e" : "#6b7280"}
							/>
						</button>

						<button
							type='button'
							onClick={() => addProductToCart(selectedVariant.id, quantity ? quantity : 1)}
							style={{
								flex: 1,
								minWidth: 0,
								height: 54,
								borderRadius: 27,
								border: 0,
								background: isAddedToCartProducts(selectedVariant.id) ? "#db1215" : "#2563eb",
								color: "#fff",
								fontSize: 16,
								fontWeight: 700,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: 8,
								boxShadow: "0 10px 30px rgba(15, 23, 42, 0.18), 0 2px 8px rgba(15, 23, 42, 0.1)",
							}}>
							<i className={isAddedToCartProducts(selectedVariant.id) ? "icon-close" : "icon-bag"} style={{ fontSize: 14 }} />
							{isAddedToCartProducts(selectedVariant.id) ? t("produs_scoate_din_cos") : t("produs_adauga_in_cos")}
						</button>
					</div>
				)}

				{selectedVariant.stock >= 1 && !isPWA && (
					<div className='tf-product-mobile-actionbar-web'>
						<button
							type='button'
							className='tf-mobile-actionbar-wishlist'
							onClick={() => addToWishlist(selectedVariant.id)}
							aria-label='Adaugă la favorite'
							style={{
								appearance: "none",
								WebkitAppearance: "none",
								boxShadow: "none",
								backgroundImage: "none",
								flexShrink: 0,
								width: 54,
								height: 54,
								borderRadius: 10,
								border: "1px solid #e5e5e5",
								background: "#fff",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}>
							<Heart
								size={22}
								fill={isAddedtoWishlist(selectedVariant.id) ? "#e53e3e" : "none"}
								color={isAddedtoWishlist(selectedVariant.id) ? "#e53e3e" : "#6b7280"}
							/>
						</button>

						<button
							type='button'
							className='tf-mobile-actionbar-cart'
							onClick={() => addProductToCart(selectedVariant.id, quantity ? quantity : 1)}
							style={{
								appearance: "none",
								WebkitAppearance: "none",
								boxShadow: "none",
								backgroundImage: "none",
								borderRadius: 10,
								border: 0,
								background: isAddedToCartProducts(selectedVariant.id) ? "#db1215" : "#2563eb",
								color: "#fff",
							}}>
							{isAddedToCartProducts(selectedVariant.id) ? <X size={20} /> : <ShoppingBag size={20} />}
							{isAddedToCartProducts(selectedVariant.id) ? t("produs_scoate_din_cos") : t("produs_adauga_in_cos")}
						</button>
					</div>
				)}
		</section>
	);
}
